import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

import { ViewEnum } from '@/types';
import { Button } from '@nf/ui';
import { BetTicket } from '../bet-ticket';
import { QuickStake } from '../quick-stake';
import { BetInput } from '../bet-input';
import { BetKeypad } from '../bet-keypad';
import { Payout } from '../payout';
import { useBetslipStore } from '@/store/betslip';
import { useTicketBet } from '@/hooks/query';
import { Notice } from '../bet-msg';
import { useTranslations } from 'next-intl';
import { VoucherBetError } from '@/features/betslip/voucher/constants';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { BookingBetContainer, BookingHandle } from '@/features/betslip/betslip-booking/components/booking-bet-container';
import { BookingPayout } from '@/features/betslip/betslip-booking/components/booking-payout';
import { SingleBetBookingTipContainer } from '@/features/betslip/betslip-booking/components/single-bet-booking-tip-container';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import { useSmpBet } from '@/features/single-match-parlay/queries/use-smp-bet';
import { SabaCoinsToggle } from '@/features/saba-coins/saba-coins-toggle';
import { BetSetting } from '../bet-setting';
import { useAppearanceStore } from '@/store/appearance';
import { BetslipService } from '@/services/betslip.service';
import { riskFreePromoService } from '@/features/promotion/single-match-category-promo/services/risk-free-promo.service';
import { AnalyseKey, useConfigsAnalyseAttribute } from '@/features/next-analyse';
import { BookingButtonProvider } from '@/features/betslip/betslip-booking/contexts/booking-button-context';
import { BookingButton } from '@/features/betslip/betslip-booking/components/booking-button';
import { BookingFeatureTip } from '@/features/betslip/betslip-booking/components/booking-feature-tip/booking-feature-tip';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { KafkaNote } from '@nf/utils-common/constants';
import { addCommasWithFix } from '@/utils/odds-utils';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';
import { Icon } from '@/components/icons/svg-icons';
import { BetslipBanner } from '@/features/promotion/golden-duel/components/betslip-banner';

const VoucherContainer = dynamic(() =>
	import('@/features/betslip/voucher/components/voucher-container').then(mod => mod.VoucherContainer)
);

const ButtonType = {
	Bet: 'Bet',
	Booking: 'Booking'
};

export const SingleBet = () => {
	const queryClient = useQueryClient();
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const { oddsType, currencyName, balance, isIndependentMode, currencySymbol } = useBetslipContext();
	const { refetch, isFetching, errorData } = useTicketBet();
	const [isBookingInitialized, setIsBookingInitialized] = useState(false);
	const mutation = useSmpBet();
	const { activeFloatBetSlip } = useFloatBetslip();
	const { activeAsideBetSlip } = useAsideBetslip();
	const { SiteConfigsData: siteConfigData } = useGetSiteConfigs();

	const bookingRef = useRef<BookingHandle>(null);
	const bookingBlocs = bookingRef.current?.getBookingBlocs();

	const [
		isExpand,
		setIsExpand,
		currentMultiTicketModel,
		canBetResult,
		checkStake,
		isBookingBet,
		useSabaCoin,
		currentMultiTickets,
		removeAllTicket
	] = useBetslipStore(state => [
		state.isExpand,
		state.setIsExpand,
		state.currentMultiTickets,
		state.canBetResult,
		state.checkStake,
		state.isBookingBet,
		state.useSabaCoin,
		state.currentMultiTickets,
		state.removeAllTicket
	]);
	const buttonType = isBookingBet ? ButtonType.Booking : ButtonType.Bet;
	const { getRef } = useConfigsAnalyseAttribute([{ key: 'placebet-single-bet', analyseKey: AnalyseKey.Refined }]);

	const getBetChoiceSummary = () => {
		const multiTickets = currentMultiTickets;
		const betChoiceArray = multiTickets.map(ticket => ticket.ChoiceValue);
		const summary = betChoiceArray.join(', ');

		return summary;
	};

	const ticket = currentMultiTicketModel[0];
	const isSmp = smpService.isSmpTicket(ticket);
	const canUseVoucher = !isBookingBet &&
		!useSabaCoin &&
		!isSmp;

	const hasBetMsg = canBetResult.innerMsg.length > 0 && canBetResult.innerMsg[0].ticketKey === ticket.key;
	const betMsg = hasBetMsg ? canBetResult.innerMsg[0] : undefined;
	const showAcceptBetterOdds = !isBookingBet && !isSmp;

	useEffect(() => {
		const setBookingInitialized = () => {
			setIsBookingInitialized(true);
		};
		document.addEventListener('booking-initialized', setBookingInitialized);

		return () => {
			document.removeEventListener('booking-initialized', setBookingInitialized);
		};
	}, []);

	// single bet第一次渲染時需要展開, float模式則不改變狀態
	const [isCheckExpand, setIsCheckExpand] = useState(false);
	useEffect(() => {
		if (!activeFloatBetSlip && !isExpand && !isCheckExpand) {
			setIsExpand(true);
			setIsCheckExpand(true);
		}
	}, []);

	useEffect(() => {
		if (errorData?.errorMessage?.startsWith('BRF')) {
			document.dispatchEvent(new CustomEvent('show-toast-msg', {
				detail: { msg: t('lbl_RPFullyRedeemed') }
			}));

			if (errorData?.errorMessage === VoucherBetError.NoVoucherAvailable) {
				riskFreePromoService.updateUsedFreeVouchersToMax(queryClient);
			}
		}
	}, [errorData?.errorMessage]);

	const onClick = () => {
		if (checkStake(isIndependentMode, oddsType)) {
			if (isSmp && !mutation.isLoading) {
				mutation.mutate(ticket);
			} else {
				if (!isFetching) {
					BetslipService.setMarsBetKafka(view, false);
					refetch();
				}
			}
		}

		if (view === ViewEnum.galaxy) {
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 8,
				action: 12
			});
		}
	};
	
	const localCurrencyName = useSabaCoin ? (siteConfigData?.EnableCurrencyDisplay ? 'SBC' : '') : currencyName;

	const getBettingAmount = (stake: number) => {
		const totalStake = addCommasWithFix(stake);
		return `${localCurrencyName} ${currencySymbol}${totalStake}`;
	}

	const onBookingSubmit = () => {
		bookingRef.current?.submitReservation();
	};

	const removeAllTicketWithStopPropagation = (event: React.MouseEvent<HTMLElement>) => {
		removeAllTicket();
		event.stopPropagation();
	}

	const activeNewSinglebetslip = activeFloatBetSlip || activeAsideBetSlip;

	// basic style scroll整個部分, aside and float scroll at slip area
	const scrollAtContainer = !activeAsideBetSlip && !activeFloatBetSlip;

	if (!isExpand && !activeAsideBetSlip && !activeFloatBetSlip) {
		return (
			<div className="bet-slip__collapse">
				<div className="bet-slip__collapse-text">
					<div dangerouslySetInnerHTML={{ __html: getBetChoiceSummary() }}></div>
				</div>
			</div>
		)
	}

	return (
		<div className={clsx(
			'bet-slip__container',
			{
				[betslipStyle.betslipContainer]: activeAsideBetSlip,
				[`scrollbar ${betslipStyle.betslipScrollContainer}`]: scrollAtContainer
			},
		)}
		>
			<div className={clsx(
				{ 'scrollbar': !scrollAtContainer },
				'bet-slip-area',
				{
					'bet-slip__independent-single': isIndependentMode && !activeFloatBetSlip,
					[betslipStyle.singleAreaContainer]: isIndependentMode || activeFloatBetSlip,
				})}
			>
				<SabaCoinsToggle />
				{hasBetMsg && !isBookingBet ? (
					<Notice
						code={betMsg?.code ?? 0}
						msg={betMsg?.msg}
						msgResourceKey={betMsg?.msgResourceKey}
					/>
				) : null}
				<BetTicket
					showVoucher={activeNewSinglebetslip && canUseVoucher}
					ticket={ticket}
					canBetResult={betMsg}
					showBetInput={activeNewSinglebetslip}
					showTextError={false}
				/>
				{ticket.isOddsChange ? <SingleBetBookingTipContainer /> : null}
			</div>
			{/* Display the booking button in a floating style. */}
			{activeNewSinglebetslip ? (
				<BookingButtonProvider>
					<div className={betslipStyle.floatBookButtonWrapper}>
						<BookingButton />
						<BookingFeatureTip />
					</div>
				</BookingButtonProvider>
			) : null}

			{/* booked bet section */}
			{isBookingBet ? (
				<BookingBetContainer
					ref={bookingRef}
					ticket={ticket}
				/>
			) : null}

			{
				!hasBetMsg || (hasBetMsg && betMsg?.canBet) ? (
					<>
						<div className="bet-slip__betting">
							{!activeNewSinglebetslip && canUseVoucher ? <VoucherContainer /> : null}

							{!activeNewSinglebetslip ? (
								<BetInput
									type="normal"
									inputCurrency={localCurrencyName}
									isParlay={false}
									canBet={true}
									ticketKey={ticket.key}
									ticketMinBet={ticket.minBet}
								/>
							) : null}

							{activeAsideBetSlip &&
								<Button className={betslipStyle.asideTrashCanIcon} variant="icon" onClick={removeAllTicketWithStopPropagation}>
									<Icon name="trash-can" />
								</Button>
							}
							{{
								[ButtonType.Bet]: (
									<button
										className={clsx(
											'btn btn--primary',
											betslipStyle.betButton
										)}
										data-disabled={isFetching || mutation.isLoading}
										onClick={onClick}
										ref={getRef('placebet-single-bet')}
									>
										<span className="btn__text">{t('lbl_bet')}</span>
										{activeNewSinglebetslip && <span className="bet-slip__betting-amount">{getBettingAmount(ticket.stake || 0)}</span>}
									</button>
								),
								[ButtonType.Booking]: (
									<Button onClick={onBookingSubmit} className={betslipStyle.bookedSubmitButton}>{t('lbl_Submit')}</Button>
								)
							}[buttonType]}
						</div>

						{!useSabaCoin && !activeNewSinglebetslip ? (
							<QuickStake
								isParlay={false}
								ticketKey={ticket.key}
								ticketStake={ticket.stake}
								ticketMaxBet={ticket.maxBet}
							/>
						) : null}
						{!activeNewSinglebetslip ? (
							<BetKeypad
								isExpand={false}
								isParlay={false}
								ticketKey={ticket.key}
								ticketStake={ticket.stake}
								ticketMaxBet={ticket.maxBet}
							/>
						) : null}
						{!activeAsideBetSlip ? (
							<BetslipBanner 
								ticket={ticket}
							/>
						) : null}
						<div className={clsx('bet-slip__footer', betslipStyle.footer)}>
							<BetSetting className={betslipStyle.galaxyBetSetting} />
							{!useSabaCoin ? (
								<div className={clsx(betslipStyle.textInfo, betslipStyle.textInfoMars)}>
									{t('lbl_AvailableFunds')}:
									<span className={betslipStyle.textSecondary}>{balance}</span>
								</div>
							) : null}

							{isBookingBet ? (
								isBookingInitialized && bookingBlocs ? (
									<BookingPayout
										oddsMinValueBloc={bookingBlocs.oddsMinValueBloc}
										oddsMaxValueBloc={bookingBlocs.oddsMaxValueBloc}
										ticketStake={ticket.stake}
										ticketPriceType={ticket.PriceType}
									/>
								) : null
							) : (
								<Payout
									payout={ticket.payout}
									betType={ticket.Bettype}
									betTeam={ticket.Betteam}
								/>
							)}

						</div>
						{showAcceptBetterOdds ? (
							<div className={betslipStyle.bottomFooter}>
								<BetSetting />
							</div>
						) : null}
					</>
				) : null
			}
		</div>
	);
};
